<script setup lang="ts" generic="T extends object">
/**
 * „Suchliste“: a search field over a multi-column list of hits — the picker
 * body for „Kunde wählen“, „Artikel suchen“ and friends. The caller owns the
 * data: it filters or fetches on `v-model:query` and hands the hits in as
 * `rows`; the list does the keyboard, the selection and the layout.
 *
 * Keyboard (in the field): ↑/↓ and PageUp/PageDown move the caret, Ctrl+Home /
 * Ctrl+End jump to the ends, Enter activates the caret row (or the first hit).
 * In the list itself Home / End work without Ctrl. Double-click activates.
 *
 * The words of the query are marked in the cells (`highlight`). The list is
 * `35vh` tall; set `--search-list-height` on the component to change that.
 *
 * Opt-in: `isRowDisabled` greys a row out (visible, but the caret skips it and
 * it cannot be picked — „aktueller Ordner“); the `cell` slot
 * (`{ row, column, text, query }`) renders a cell richly (a second line, an
 * icon, a badge) instead of the plain text.
 */
import { computed, nextTick, useTemplateRef } from 'vue';
import NxHighlight from '../Highlight/NxHighlight.vue';
import NxSearchInput from '../Inputs/NxSearchInput.vue';
import { useListboxNav } from './useListboxNav';

/** One column of the list. */
export interface NxSearchListColumn<R> {
  /** Field of the row, also the column's identity. */
  key: string;
  /** Header text. */
  label: string;
  /** Grid track, e.g. `'minmax(0, 2fr)'` or `'calc(var(--nxt-size-unit) * 5)'`. Default `minmax(0, 1fr)`. */
  width?: string;
  /** Tabular, monospaced figures (numbers, ids). */
  mono?: boolean;
  /** Secondary tone (ids, codes). */
  muted?: boolean;
  /** Right-align (amounts). */
  align?: 'start' | 'end';
  /** Cell text when it is not simply `row[key]`. */
  value?: (row: R) => unknown;
}

const props = withDefaults(
  defineProps<{
    rows: readonly T[];
    columns: readonly NxSearchListColumn<T>[];
    /** Row identity: a field name or a function. Default `'id'`. */
    rowKey?: string | ((row: T) => string | number);
    loading?: boolean;
    /** Placeholder (and accessible name) of the search field. */
    placeholder?: string;
    /** Accessible name of the list. */
    ariaLabel?: string;
    emptyText?: string;
    loadingText?: string;
    /** Hide the column header row. */
    hideHeader?: boolean;
    /** Mark the query's words in the cells. Default `true`. */
    highlight?: boolean;
    /** Rows that stay visible but cannot be picked (the caret skips them). */
    isRowDisabled?: (row: T) => boolean;
    /** Grow the results inside a bounded flex column, retaining 35vh as the initial basis. */
    fill?: boolean;
  }>(),
  {
    rowKey: 'id',
    loading: false,
    placeholder: 'Suche',
    ariaLabel: 'Treffer',
    emptyText: 'Keine Treffer',
    loadingText: 'Wird geladen …',
    hideHeader: false,
    highlight: true,
    isRowDisabled: undefined,
  }
);

const query = defineModel<string>('query', { default: '' });
const selected = defineModel<T | null>('selected', { default: null });

const emit = defineEmits<{
  /** A row was picked by click (or with Enter, before `activate`). */
  select: [row: T];
  /** Double-click or Enter — the caller usually takes the row and closes. */
  activate: [row: T];
}>();

const keyOf = (row: T): string | number => {
  const key = props.rowKey;
  if (typeof key === 'function') return key(row);
  return (row as Record<string, unknown>)[key] as string | number;
};
const selectedKey = computed(() => (selected.value === null ? null : keyOf(selected.value)));
const isDisabled = (row: T): boolean => props.isRowDisabled?.(row) ?? false;

const rowsEl = useTemplateRef<HTMLElement>('rows-el');
const searchRef = useTemplateRef<{ inputElement: HTMLInputElement | null }>('search');

// While loading the rows are not rendered: navigating or activating them would
// pick a stale hit and point `aria-activedescendant` at a missing option.
const nav = useListboxNav(() => (props.loading ? [] : props.rows), {
  isDisabled: (row) => isDisabled(row),
  onMove: (row, index) => {
    // Selection follows the caret, like a native single-select list.
    selected.value = row;
    void nextTick(() =>
      rowsEl.value
        ?.querySelector<HTMLElement>(`[id="${nav.optionId(index)}"]`)
        ?.scrollIntoView?.({ block: 'nearest' })
    );
  },
  onSelect: (row) => activate(row),
});

const gridTemplate = computed(() =>
  props.columns.map((column) => column.width ?? 'minmax(0, 1fr)').join(' ')
);

function cellText(row: T, column: NxSearchListColumn<T>): string {
  const raw = column.value ? column.value(row) : (row as Record<string, unknown>)[column.key];
  if (raw === null || raw === undefined || String(raw).trim() === '') return '—';
  return String(raw);
}

function select(row: T, index: number): void {
  if (isDisabled(row)) return;
  nav.activeIndex.value = index;
  selected.value = row;
  emit('select', row);
}

function activate(row: T): void {
  if (isDisabled(row)) return;
  selected.value = row;
  emit('select', row);
  emit('activate', row);
}

function onSearchKeydown(event: KeyboardEvent): void {
  nav.onKeydown(event, { homeEnd: false });
}

function onListKeydown(event: KeyboardEvent): void {
  nav.onKeydown(event);
}

/** Puts the caret into the search field (call it when the host dialog has opened). */
function focusSearch(): void {
  searchRef.value?.inputElement?.focus();
}

defineExpose({
  focusSearch,
  /** Clears the keyboard caret (the selection stays). */
  resetCaret: nav.reset,
  inputElement: computed(() => searchRef.value?.inputElement ?? null),
});
</script>

<template>
  <div class="nx-search-list" :class="{ 'is-fill': props.fill }">
    <NxSearchInput
      ref="search"
      v-model="query"
      class="nx-search-list__search"
      role="combobox"
      aria-autocomplete="list"
      aria-expanded="true"
      :aria-controls="nav.listId"
      :aria-activedescendant="nav.activeId.value"
      :aria-label="placeholder"
      :placeholder="placeholder"
      data-testid="nx-search-list-input"
      @keydown="onSearchKeydown"
    />
    <div class="nx-search-list__table" :style="{ '--search-list-cols': gridTemplate }">
      <div v-if="!hideHeader" class="nx-search-list__head" aria-hidden="true">
        <span
          v-for="column in columns"
          :key="column.key"
          :class="{ 'is-end': column.align === 'end' }"
        >
          {{ column.label }}
        </span>
      </div>
      <div
        :id="nav.listId"
        ref="rows-el"
        class="nx-search-list__rows"
        role="listbox"
        tabindex="-1"
        :aria-label="ariaLabel"
        :aria-busy="loading ? 'true' : undefined"
        :aria-activedescendant="nav.activeId.value"
        @keydown="onListKeydown"
      >
        <template v-if="!loading">
          <div
            v-for="(row, index) in rows"
            :id="nav.optionId(index)"
            :key="keyOf(row)"
            class="nx-search-list__row"
            :class="{
              'is-active': nav.activeIndex.value === index,
              'is-selected': selectedKey !== null && selectedKey === keyOf(row),
              'is-disabled': isDisabled(row),
            }"
            role="option"
            :aria-selected="selectedKey !== null && selectedKey === keyOf(row)"
            :aria-disabled="isDisabled(row) || undefined"
            @click="select(row, index)"
            @dblclick="activate(row)"
          >
            <span
              v-for="column in columns"
              :key="column.key"
              class="nx-search-list__cell"
              :class="{
                'is-mono': column.mono,
                'is-muted': column.muted,
                'is-end': column.align === 'end',
              }"
            >
              <slot
                name="cell"
                :row="row"
                :column="column"
                :text="cellText(row, column)"
                :query="query"
              >
                <NxHighlight
                  v-if="highlight && query"
                  :text="cellText(row, column)"
                  :query="query"
                />
                <template v-else>{{ cellText(row, column) }}</template>
              </slot>
            </span>
          </div>
        </template>
      </div>
      <div v-if="loading" class="nx-search-list__state" role="status">{{ loadingText }}</div>
      <div v-else-if="rows.length === 0" class="nx-search-list__state" role="status">
        {{ emptyText }}
      </div>
    </div>
  </div>
</template>

<style scoped>
.nx-search-list {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-sm);
  min-width: 0;
}

.nx-search-list.is-fill {
  flex: 1 1 auto;
  min-height: 0;

  .nx-search-list__search {
    flex: none;
  }

  .nx-search-list__table {
    flex: 1 1 auto;
    min-block-size: 0;
  }
}

.nx-search-list__search {
  width: 100%;
}

.nx-search-list__table {
  display: flex;
  flex-direction: column;
  block-size: var(--search-list-height, 35vh);
  min-block-size: 12em;
  overflow: auto;
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-surface-bg);
}

.nx-search-list__head,
.nx-search-list__row {
  display: grid;
  grid-template-columns: var(--search-list-cols);
  gap: var(--nxt-space-sm);
  align-items: center;
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  font-size: var(--nxt-font-size-sm);
}

.nx-search-list__head {
  position: sticky;
  top: 0;
  z-index: var(--nxt-z-sticky);
  background: var(--nxt-surface-sunken-bg);
  border-bottom: var(--nxt-hairline) solid var(--nxt-border);
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-bold);
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.nx-search-list__rows {
  display: flex;
  flex-direction: column;
  outline: none;
}

.nx-search-list__rows:focus-visible {
  box-shadow: inset 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}

.nx-search-list__row {
  border-bottom: var(--nxt-hairline) solid var(--nxt-border);
  color: var(--nxt-fg);
  cursor: pointer;
  user-select: none;
  transition: background-color var(--nxt-motion-fast) var(--nxt-ease-out);
}

.nx-search-list__row:last-child {
  border-bottom: 0;
}

.nx-search-list__row:hover {
  background: color-mix(in srgb, var(--nxt-brand-bg) 6%, transparent);
}

.nx-search-list__row.is-active {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: calc(var(--nxt-hairline) * -2);
}

.nx-search-list__row.is-selected {
  background: color-mix(in srgb, var(--nxt-brand-bg) 12%, transparent);
}

.nx-search-list__row.is-disabled {
  color: var(--nxt-fg-muted);
  cursor: default;
}

.nx-search-list__row.is-disabled:hover {
  background: transparent;
}

.nx-search-list__head > span,
.nx-search-list__cell {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nx-search-list__cell:first-child:not(.is-muted),
.nx-search-list__cell.is-muted + .nx-search-list__cell:not(.is-muted) {
  /* Only the leading name column carries weight; the rest reads as detail. */
  font-weight: var(--nxt-font-weight-semibold);
}

.nx-search-list__cell.is-muted {
  color: var(--nxt-fg-muted);
  font-weight: var(--nxt-font-weight-regular);
}

.nx-search-list__cell.is-mono {
  font-family: var(--nxt-font-mono);
  font-variant-numeric: tabular-nums;
}

.is-end {
  text-align: end;
}

.nx-search-list__state {
  padding: var(--nxt-space-md) var(--nxt-space-sm);
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-sm);
}

@media (prefers-reduced-motion: reduce) {
  .nx-search-list__row {
    transition: none;
  }
}
</style>
